import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useMatches } from 'react-router';

import type {
  BreadcrumbResolver,
  RouteBreadcrumbs,
  RouteHandle,
} from '~/routing/types';

import { isRouteHandle } from '~/routing/isRouteHandle';

// A route "owns" its breadcrumb once it declares one at all; the deepest such
// match wins over shallower ones. `'self'` and an empty array both count as
// owning it: they say the page has no ancestors, so its `title` is the whole
// trail.
function hasBreadcrumbs(
  value: unknown,
): value is RouteHandle & { breadcrumbs: RouteBreadcrumbs } {
  if (!isRouteHandle(value)) {
    return false;
  }
  if (
    value.breadcrumbs === 'self' ||
    value.breadcrumbs === 'deferred' ||
    value.breadcrumbs === 'none'
  ) {
    return true;
  }
  return Array.isArray(value.breadcrumbs);
}

/**
 * A resolved breadcrumb ready to render. A `static` crumb carries its final
 * `text`; a `dynamic` crumb carries the `resolver` key whose label is resolved
 * (and may load asynchronously) at render time by `AppBreadcrumb`. `pathname`,
 * when present, is the link target; the last crumb is always the current page.
 */
export type BreadcrumbItem =
  | { kind: 'static'; text: string; pathname?: string }
  | { kind: 'dynamic'; resolver: BreadcrumbResolver; pathname?: string };

/**
 * The active route's breadcrumb. `deferred` means the page owns the trail and
 * will portal it in via `DeferredBreadcrumb`, so `AppBreadcrumb` renders only
 * the slot; `resolved` carries the crumbs for `AppBreadcrumb` to render.
 */
export type BreadcrumbTrail =
  | { mode: 'deferred' }
  | { mode: 'resolved'; crumbs: BreadcrumbItem[] };

export function useRouteBreadcrumbs(): BreadcrumbTrail {
  const intl = useIntl();
  const matches = useMatches();

  return useMemo(() => {
    // Prefer an explicit declaration: the deepest matched route that declares
    // one owns the whole breadcrumb (ancestors → self), so shallower matches are
    // ignored.
    const trailMatch = [...matches]
      .reverse()
      .find((match) => hasBreadcrumbs(match.handle));

    if (trailMatch && hasBreadcrumbs(trailMatch.handle)) {
      const { breadcrumbs } = trailMatch.handle;

      if (breadcrumbs === 'deferred') {
        return { mode: 'deferred' };
      }

      // The route renders no breadcrumb bar, so it has no trail rather than a
      // fallback one — resolving to zero crumbs keeps the bar empty.
      if (breadcrumbs === 'none') {
        return { mode: 'resolved', crumbs: [] };
      }

      // No ancestors, so the `title` crumb appended below is the whole trail.
      const ancestors = breadcrumbs === 'self' ? [] : breadcrumbs;

      const crumbs = ancestors.map((crumb): BreadcrumbItem => {
        const pathname = crumb.path
          ? generatePath(crumb.path, trailMatch.params)
          : undefined;

        if ('resolver' in crumb) {
          return { kind: 'dynamic', resolver: crumb.resolver, pathname };
        }

        return {
          kind: 'static',
          text: intl.formatMessage(crumb.title),
          pathname,
        };
      });

      // An unlinked last crumb already names the current page; anything else is
      // all ancestors, so `title` supplies the leaf. An empty trail is all
      // ancestors too — there just aren't any.
      const namesCurrentPage =
        crumbs.length > 0 && crumbs.at(-1)?.pathname === undefined;

      return {
        mode: 'resolved',
        crumbs: namesCurrentPage
          ? crumbs
          : [
              ...crumbs,
              {
                kind: 'static',
                text: intl.formatMessage(trailMatch.handle.title),
              },
            ],
      };
    }

    // Fallback: one crumb per matched route from its static title, deduping
    // consecutive identical labels (unchanged behavior for non-opted-in routes).
    const matched = matches.flatMap((match) => {
      if (!isRouteHandle(match.handle)) {
        return [];
      }

      return [
        {
          text: intl.formatMessage(match.handle.title),
          pathname: match.pathname,
        },
      ];
    });

    return {
      mode: 'resolved',
      crumbs: matched
        .filter(
          (crumb, index) =>
            index === 0 || crumb.text !== matched[index - 1].text,
        )
        .map(
          ({ text, pathname }): BreadcrumbItem => ({
            kind: 'static',
            text,
            pathname,
          }),
        ),
    };
  }, [matches, intl]);
}
